import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Sparkles } from "lucide-react";
import { IVRPanel } from "../components/IVRPanel";
import FinalAssessment from "../components/FinalAssessment";

const RECRUITER_ROLES = ["recruiter", "hiring_manager", "group_hr_head", "entity_hr_head", "super_admin", "onboarding_specialist"];

function ScoreBar({ label, value }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1"><span className="uppercase tracking-wider text-muted-foreground">{label}</span><span className="font-bold">{value}</span></div>
      <div className="h-1.5 bg-secondary"><div className="h-full bg-[#0A251D]" style={{ width: `${value}%` }} /></div>
    </div>
  );
}

export default function CandidateDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [cand, setCand] = useState(null);
  const [req, setReq] = useState(null);
  const [ivs, setIvs] = useState([]);
  const [offers, setOffers] = useState([]);
  const [stages, setStages] = useState([]);
  const [screening, setScreening] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/candidates/${id}`);
    setCand(data);
    if (data.requisition_id) api.get(`/requisitions/${data.requisition_id}`).then((r) => setReq(r.data)).catch(() => {});
    api.get(`/interviews?candidate_id=${id}`).then((r) => setIvs(r.data)).catch(() => {});
    api.get("/offers").then((r) => setOffers(r.data.filter((o) => o.candidate_id === id))).catch(() => {});
  }, [id]);
  useEffect(() => { load(); api.get("/candidates/stages").then((r) => setStages(r.data)).catch(() => {}); }, [load]);

  const screen = async () => {
    setScreening(true);
    try {
      const { data } = await api.post(`/candidates/${id}/screen`);
      setCand(data);
      toast.success(`AI screening complete: ${data.ai_screening.overall_score}/100`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setScreening(false); }
  };

  const moveStage = async (stage) => {
    try {
      const { data } = await api.post(`/candidates/${id}/stage`, { stage, note: "" });
      setCand(data);
      toast.success(`Moved to ${stage}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const startInterviews = async () => {
    try {
      await api.post(`/candidates/${id}/start-interviews`);
      toast.success("Interview process started — Round 1 created, schedule it from Interviews");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!cand) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const canAct = RECRUITER_ROLES.includes(user?.role);
  const s = cand.ai_screening;

  const journey = [
    { label: "AI Screened", done: !!s, detail: s ? `${s.overall_score}/100` : "pending" },
    { label: "AI Outreach", done: !!cand.outreach, detail: cand.outreach ? "message sent" : "—" },
    { label: "Interest", done: cand.interest_status === "interested", detail: cand.interest_status || "awaiting" },
    { label: "Shortlisted", done: ["shortlisted", "interview", "offer", "hired"].includes(cand.stage), detail: "" },
    { label: "Interviews", done: ["interview", "offer", "hired"].includes(cand.stage), detail: ivs.length ? `${ivs.length} round(s)` : "" },
    { label: "Offer", done: ["offer", "hired"].includes(cand.stage), detail: "" },
  ];

  return (
    <div data-testid="candidate-detail-page">
      <Link to="/candidates" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-muted-foreground hover:text-[#0A251D]"><ArrowLeft size={13} /> Candidates</Link>
      <div className="flex items-start justify-between flex-wrap gap-4 mt-2">
        <div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter">{cand.name}</h1>
          <div className="text-sm text-muted-foreground mt-1">{cand.email} · {cand.phone} · {cand.experience_years} yrs · {cand.location}</div>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider bg-[#0A251D] text-white px-2 py-1 font-semibold" data-testid="candidate-stage-badge">{cand.stage}</span>
            {req && <Link to={`/requisitions/${req.id}`} className="text-xs text-[#0A251D] hover:underline">{req.title}</Link>}
            <span className="text-xs text-muted-foreground capitalize">via {cand.source}</span>
          </div>
        </div>
        {canAct && (
          <div className="flex items-center gap-2">
            <select data-testid="stage-move-select" value={cand.stage} onChange={(e) => moveStage(e.target.value)} className="border px-3 py-2 text-sm bg-white">
              {stages.map((st) => <option key={st.key} value={st.key}>{st.label}</option>)}
            </select>
            <button data-testid="ai-screen-button" onClick={screen} disabled={screening || !cand.resume_text}
              className="flex items-center gap-2 bg-[#E05A47] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#C74936] transition-colors duration-150 disabled:opacity-60">
              <Sparkles size={14} /> {screening ? "Claude is screening…" : s ? "Re-screen with AI" : "AI Screen vs JD"}
            </button>
          </div>
        )}
      </div>

      {/* AI Recruitment Journey strip */}
      <div className="border bg-white rounded-xl p-5 mt-6" data-testid="ai-journey-strip">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-0 flex-wrap">
            {journey.map((j, i) => (
              <div key={j.label} className="flex items-center">
                <div className="text-center px-3">
                  <div className={`w-7 h-7 mx-auto rounded-full flex items-center justify-center text-[11px] font-black ${j.done ? "bg-[#0A251D] text-white" : "bg-secondary text-muted-foreground"}`}>{i + 1}</div>
                  <div className={`text-[9px] uppercase tracking-wider mt-1 font-bold ${j.done ? "text-[#0A251D]" : "text-muted-foreground"}`}>{j.label}</div>
                  {j.detail && <div className="text-[10px] text-[#E05A47] font-bold">{j.detail}</div>}
                </div>
                {i < journey.length - 1 && <div className={`w-8 h-0.5 ${journey[i + 1].done ? "bg-[#0A251D]" : "bg-border"}`} />}
              </div>
            ))}
          </div>
          {canAct && cand.stage === "shortlisted" && ivs.length === 0 && (
            <button data-testid="start-interviews-button" onClick={startInterviews}
              className="bg-[#E05A47] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#C74936] transition-colors duration-150">
              Start Interview Process →
            </button>
          )}
        </div>
        {cand.outreach && (
          <div className="mt-3 border-t pt-3 text-xs text-muted-foreground">
            <span className="font-bold text-[#0A251D]">AI first message:</span> “{cand.outreach.message}” — sent via portal + WhatsApp (simulated)
            {cand.interest_status && <span className="ml-2 uppercase tracking-wider font-bold text-[#E05A47]">[{cand.interest_status}]</span>}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 space-y-4">
          {s && (
            <div className="border bg-white rounded-xl p-6" data-testid="ai-screening-panel">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">AI Screening — Resume vs JD</div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-bold ${
                  s.recommendation?.includes("yes") ? "bg-emerald-100 text-emerald-800" : s.recommendation === "maybe" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700"}`}>
                  {s.recommendation?.replace("_", " ")}
                </span>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-center shrink-0">
                  <div className="font-heading font-black text-5xl text-[#0A251D]" data-testid="ai-overall-score">{s.overall_score}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">/ 100</div>
                </div>
                <div className="flex-1 space-y-3">
                  <ScoreBar label="Skills Match" value={s.skills_match} />
                  <ScoreBar label="Experience Match" value={s.experience_match} />
                  <ScoreBar label="Education Match" value={s.education_match} />
                </div>
              </div>
              <p className="text-sm mt-4 leading-relaxed">{s.summary}</p>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold mb-1.5">Strengths</div>
                  {s.strengths?.map((x, i) => <div key={i} className="text-sm py-0.5">+ {x}</div>)}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-red-700 font-bold mb-1.5">Gaps</div>
                  {s.gaps?.map((x, i) => <div key={i} className="text-sm py-0.5">− {x}</div>)}
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-4 border-t pt-3">Screened by {s.screened_by} · Model: Claude Sonnet 4.6 via TalentOS AI Layer</div>
            </div>
          )}
          <FinalAssessment candidateId={cand.id} />
          <IVRPanel candidateId={cand.id} canAct={canAct} />
          <div className="border bg-white rounded-xl p-6">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">Resume</div>
            <div className="text-sm leading-relaxed whitespace-pre-wrap">{cand.resume_text || "No resume text on file."}</div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="border bg-white rounded-xl p-6">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">Profile</div>
            {[["Current Company", cand.current_company || "—"], ["Notice Period", cand.notice_period || "—"],
              ["Expected CTC", cand.expected_ctc || "—"], ["Source", cand.source]].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b last:border-0 py-2 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-medium capitalize">{v}</span></div>
            ))}
          </div>

          <div className="border bg-white rounded-xl p-6" data-testid="candidate-interviews-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Interviews ({ivs.length})</div>
              <Link to={`/interviews?candidate=${cand.id}`} data-testid="view-candidate-interviews" className="text-xs font-bold text-[#E05A47] hover:underline">View all →</Link>
            </div>
            {ivs.length === 0 && <div className="text-sm text-muted-foreground">No interviews scheduled.</div>}
            {ivs.slice(0, 4).map((iv) => (
              <div key={iv.id} className="border-b last:border-0 py-2">
                <div className="text-sm font-semibold">{iv.round_name}</div>
                <div className="text-xs text-muted-foreground capitalize">{iv.status} · {new Date(iv.scheduled_at).toLocaleString()}
                  {iv.scorecards?.length > 0 && ` · ${iv.scorecards[0].recommendation?.replace(/_/g, " ")}`}
                </div>
              </div>
            ))}
          </div>

          <div className="border bg-white rounded-xl p-6" data-testid="candidate-offers-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Offers ({offers.length})</div>
              <Link to={`/offers?candidate=${cand.id}`} data-testid="view-candidate-offers" className="text-xs font-bold text-[#E05A47] hover:underline">View all →</Link>
            </div>
            {offers.length === 0 && <div className="text-sm text-muted-foreground">No offers yet.</div>}
            {offers.map((o) => (
              <div key={o.id} className="border-b last:border-0 py-2">
                <div className="text-sm font-semibold">₹{o.ctc_lpa} LPA — {o.designation || "Offer"}</div>
                <div className="text-xs text-muted-foreground capitalize">{o.status} · Joining {o.joining_date}</div>
              </div>
            ))}
          </div>

          <div className="border bg-white rounded-xl p-6" data-testid="stage-history-panel">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">Stage History</div>
            {cand.stage_history?.slice().reverse().map((h, i) => (
              <div key={i} className="border-b last:border-0 py-2">
                <div className="text-sm font-semibold capitalize">{h.stage}</div>
                <div className="text-xs text-muted-foreground">by {h.by}{h.note ? ` — ${h.note}` : ""}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
